/** @graphql cc **/
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import Spinner from '@atlaskit/spinner/spinner';
import PagesIcon from '@atlaskit/icon/core/pages';
import TasksIcon from '@atlaskit/icon/core/tasks';
import { SpotlightTarget } from '@atlaskit/onboarding';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { PageTree } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey, usePageContentId, useContentType } from '@confluence/page-context';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useHoverPageCard } from '@confluence/page-card/entry-points/useHoverPageCard.ts';
import { useIsSuperAdmin } from '@confluence/super-admin/entry-points/AdminKeyQuery.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { SpacePersistentCollapsibleSectionLoader } from '@confluence/collapsible-section/entry-points/SpacePersistentCollapsibleSectionLoader.ts';
import { useSpacePersistentExpansion } from '@confluence/collapsible-section/entry-points/useSpacePersistentExpansion.ts';
import { SpaceHoverPageCard } from '@confluence/page-card/entry-points/SpaceHoverPageCard.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';

import { useSpaceViewsOptions } from './useSpaceViewsOptions.tsx';
import { FlexCenter } from './PresentationalComponents.tsx';
import { useSpaceViewsExperienceTracking } from './useSpaceViewsExperienceTracking.ts';
import type {
	SpaceViewsControllerQuery,
	SpaceViewsControllerQueryVariables,
} from './__types__/SpaceViewsControllerQuery.ts';

const SpaceViews = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViews" */ './SpaceViews.tsx')).SpaceViews,
});

const PagesItemActions = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-PagesItemActions" */ './PagesItemActions.tsx'))
			.PagesItemActions,
});

const ContentTreeSearchLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ContentTreeSearch" */ './ContentTreeSearch.tsx'))
			.ContentTreeSearch,
});

const i18n = defineMessages({
	contentTitle: {
		id: 'space-views.collapsible-section.title',
		defaultMessage: 'Content',
		description: 'Header for all content section',
	},
	multiSelectContentTitle: {
		id: 'space-views.collapsible-section.title.multi-select',
		defaultMessage: 'Multi-select content',
		description: 'Header for all content section when multi-select is active',
	},
	contentTooltip: {
		id: 'space-views.collapsible-section.title.tooltip',
		defaultMessage: 'Organize content in this space',
		description: 'Tooltip when hovering over content space sidebar collapsible section',
	},
});

type SpaceViewsControllerProps = {
	onDragDropSuccess?: (contentId: any) => void;
	isPeekingFromBlogs?: boolean;
	spaceHasUserContent: boolean;
	homepageId?: string;
	onContentTreeLoadComplete?: () => void;
	pageTreeFinishedLoading: boolean;
};

const SpaceViewsControllerComponent: FC<SpaceViewsControllerProps> = ({
	onDragDropSuccess,
	isPeekingFromBlogs,
	spaceHasUserContent,
	homepageId,
	onContentTreeLoadComplete,
	pageTreeFinishedLoading,
}) => {
	const { formatMessage } = useIntl();
	const { isSuperAdmin } = useIsSuperAdmin();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [pageContextContentType] = useContentType();
	const { isLicensed, edition } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();

	const {
		spaceViewsSort,
		setSpaceViewsSort,
		queryLoading: persistenceLoading,
		queryError: persistenceError,
		mutationLoading: persistenceUpdateLoading,
		mutationError: persistenceUpdateError,
	} = useSpaceViewsOptions(
		// @ts-ignore FIXME: `spaceKey` can be `undefined` here, and needs proper handling
		spaceKey,
		isLicensed,
	);
	const forceExpandBecauseViewingContentTree = pageContextContentType !== 'blogpost';

	const [isPageTreeLoading, setIsPageTreeLoading] = useState<boolean>(true);
	const [pageTreeError, setPageTreeError] = useState<ApolloErrorInterop | undefined>();
	const [isSpaceViewsLoading, setIsSpaceViewsLoading] = useState<boolean>(true);
	const [contentTreeSize, setContentTreeSize] = useState<number>(0);
	const [spaceViewsError, setSpaceViewsError] = useState<ApolloErrorInterop | undefined>();
	const isPageTree = spaceViewsSort === 'TREE';
	const [isMultiSelectActive] = useIsMultiSelectActive();

	// Ref to the space views menu trigger button in the content header
	const spaceViewsMenuTriggerRef = useRef<HTMLButtonElement>(null);

	const {
		data,
		loading: isSpaceAdminQueryLoading,
		error: isSpaceAdminQueryError,
	} = useQuery<SpaceViewsControllerQuery, SpaceViewsControllerQueryVariables>(
		gql`
			query SpaceViewsControllerQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: {
				spaceKey: spaceKey ?? '',
			},
		},
	);

	const loading =
		persistenceLoading ||
		(isPageTree ? isPageTreeLoading : isSpaceViewsLoading) ||
		isSpaceAdminQueryLoading;
	const error = pageTreeError || spaceViewsError || isSpaceAdminQueryError;

	const isSpaceAdmin = Boolean(data?.space?.currentUser?.isAdmin);
	const isSpaceAdminOrSuperAdmin = isSpaceAdmin || isSuperAdmin;

	const { trackedSetSpaceViewsSort, makeTrackedFetchMore } = useSpaceViewsExperienceTracking({
		loading,
		error,
		persistenceError,
		persistenceUpdateLoading,
		persistenceUpdateError,
		spaceViewsSort,
		setSpaceViewsSort,
		isSpaceAdmin,
	});

	const {
		onEnterHoverTarget: showHoverPageCardOnEnterHoverTarget,
		closeAllHoverPageCards,
		startCloseHoverPageCardTimer,
		clearHoverPageCardTimers,
		showHoverPageCardForItem,
		isHoverPageCardOptedIn,
		onLeaveHoverTarget,
		onBlurHoverTarget,
	} = useHoverPageCard(spaceViewsSort);

	const [shouldRenderAfterIcon, setShouldRenderAfterIcon] = useState(false);
	const onEnterHoverTarget = useCallback(
		({
			itemID,
			isQuickActionsFocused,
			hoverPageCardTriggerRef,
			isContextualCreateFocused,
		}: {
			itemID: string;
			isQuickActionsFocused: boolean;
			hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
			isContextualCreateFocused?: boolean;
		}) => {
			showHoverPageCardOnEnterHoverTarget({
				itemID,
				isQuickActionsFocused,
				hoverPageCardTriggerRef,
				isContextualCreateFocused,
			});
			setShouldRenderAfterIcon(true);
		},
		[showHoverPageCardOnEnterHoverTarget, setShouldRenderAfterIcon],
	);

	const { isExpanded, onExpansionToggle } = useSpacePersistentExpansion({
		sectionName: 'contentTree',
		collapsedByDefault: !forceExpandBecauseViewingContentTree,
		forceExpand: forceExpandBecauseViewingContentTree,
	});

	const handleSpaceViewCleared = useCallback(() => {
		// Focus the space views menu trigger button in the content header using React ref
		if (spaceViewsMenuTriggerRef.current) {
			spaceViewsMenuTriggerRef.current.focus();
		}
	}, [spaceViewsMenuTriggerRef]);

	const renderContentTree = useMemo(() => {
		if (persistenceLoading) {
			return (
				<FlexCenter>
					<Spinner size="medium" />
				</FlexCenter>
			);
		}

		const hoverPageCardProps = {
			onEnterHoverTarget,
			closeAllHoverPageCards,
			isHoverPageCardOptedIn,
			onLeaveHoverTarget,
			onBlurHoverTarget,
		};

		if (isPageTree) {
			return (
				<ContentTreeSearchLoader
					contentTreeSize={contentTreeSize}
					shouldRenderAfterIcon={shouldRenderAfterIcon}
					selection={spaceViewsSort}
					setSelection={trackedSetSpaceViewsSort}
					isLicensed={isLicensed}
					isSpaceAdminOrSuperAdmin={isSpaceAdminOrSuperAdmin}
					{...hoverPageCardProps}
				>
					<PageTree
						onDragDropSuccess={onDragDropSuccess}
						isPeekingFromBlogs={isPeekingFromBlogs}
						isSuperAdmin={isSuperAdmin}
						setIsPageTreeLoading={setIsPageTreeLoading}
						setPageTreeError={setPageTreeError}
						shouldRenderAfterIcon={shouldRenderAfterIcon}
						homepageId={homepageId}
						setContentTreeSize={setContentTreeSize}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
						{...hoverPageCardProps}
					/>
				</ContentTreeSearchLoader>
			);
		}
		return (
			<SpaceViews
				spaceViewsSort={spaceViewsSort}
				setIsSpaceViewsLoading={setIsSpaceViewsLoading}
				setSpaceViewsError={setSpaceViewsError}
				trackedSetSpaceViewsSort={trackedSetSpaceViewsSort}
				makeTrackedFetchMore={makeTrackedFetchMore}
				spaceKey={spaceKey}
				contentId={contentId}
				shouldRenderAfterIcon={shouldRenderAfterIcon}
				contentTreeSize={contentTreeSize}
				onContentTreeLoadComplete={onContentTreeLoadComplete}
				onSpaceViewCleared={handleSpaceViewCleared}
				{...hoverPageCardProps}
			/>
		);
	}, [
		persistenceLoading,
		onEnterHoverTarget,
		closeAllHoverPageCards,
		isHoverPageCardOptedIn,
		onLeaveHoverTarget,
		onBlurHoverTarget,
		isPageTree,
		spaceViewsSort,
		trackedSetSpaceViewsSort,
		makeTrackedFetchMore,
		spaceKey,
		contentId,
		shouldRenderAfterIcon,
		contentTreeSize,
		onContentTreeLoadComplete,
		isLicensed,
		onDragDropSuccess,
		isPeekingFromBlogs,
		isSuperAdmin,
		homepageId,
		isSpaceAdminOrSuperAdmin,
		handleSpaceViewCleared,
	]);

	if (isNav4Enabled) {
		return (
			<ExpandableMenuItem isExpanded={isExpanded} onExpansionToggle={onExpansionToggle}>
				<SpotlightTarget name="nav4-spotlight-3">
					<ExpandableMenuItemTrigger
						elemBefore={
							isMultiSelectActive ? (
								<TasksIcon label="" color="currentColor" />
							) : (
								<PagesIcon label="" color="currentColor" />
							)
						}
						actionsOnHover={
							<PagesItemActions
								isLicensed={isLicensed}
								edition={edition ?? undefined}
								selection={spaceViewsSort}
								setSelection={trackedSetSpaceViewsSort}
								spaceKey={spaceKey || ''}
								expandContentSection={() => onExpansionToggle(true)}
								onItemClick={() => onExpansionToggle(true)}
								spaceHasUserContent={spaceHasUserContent}
								pageTreeFinishedLoading={pageTreeFinishedLoading}
								isSpaceAdminOrSuperAdmin={isSpaceAdminOrSuperAdmin}
								spaceViewsMenuTriggerRef={spaceViewsMenuTriggerRef}
							/>
						}
					>
						{isMultiSelectActive
							? formatMessage(i18n.multiSelectContentTitle)
							: formatMessage(i18n.contentTitle)}
					</ExpandableMenuItemTrigger>
				</SpotlightTarget>
				<ExpandableMenuItemContent>
					{renderContentTree}
					{/* we place the popup here and use a portal to adjust the desired location.
        			This way we don't need to render a popup per pageTreeItem
       				The component should only rerender whenever showHoverPageCardForItem changes.
        			*/}
					{isHoverPageCardOptedIn && showHoverPageCardForItem.itemID ? (
						<SpaceHoverPageCard
							showHoverPageCardForItem={showHoverPageCardForItem}
							closeAllHoverPageCards={closeAllHoverPageCards}
							clearHoverPageCardTimers={clearHoverPageCardTimers}
							startCloseHoverPageCardTimer={startCloseHoverPageCardTimer}
							onBlurHoverTarget={onBlurHoverTarget}
							shouldUseAutomationDiscovery
							key={showHoverPageCardForItem.itemID}
						/>
					) : null}
					{persistenceError && <ErrorDisplay error={persistenceError} />}
				</ExpandableMenuItemContent>
			</ExpandableMenuItem>
		);
	}

	return (
		<SpacePersistentCollapsibleSectionLoader
			renderAfterIconButton={(setHeaderExpandedState) => (
				<PagesItemActions
					isLicensed={isLicensed}
					selection={spaceViewsSort}
					setSelection={trackedSetSpaceViewsSort}
					spaceKey={spaceKey || ''}
					onItemClick={() => setHeaderExpandedState?.(true)}
					spaceHasUserContent={spaceHasUserContent}
					pageTreeFinishedLoading={pageTreeFinishedLoading}
					isSpaceAdminOrSuperAdmin={isSpaceAdminOrSuperAdmin}
					spaceViewsMenuTriggerRef={spaceViewsMenuTriggerRef}
				/>
			)}
			headerTitle={formatMessage(i18n.contentTitle)}
			sectionName="contentTree"
			tooltipContent={formatMessage(i18n.contentTooltip)}
		>
			{renderContentTree}
			{/* we place the popup here and use a portal to adjust the desired location.
        This way we don't need to render a popup per pageTreeItem
        The component should only rerender whenever showHoverPageCardForItem changes.
        */}
			{isHoverPageCardOptedIn && showHoverPageCardForItem.itemID ? (
				<SpaceHoverPageCard
					showHoverPageCardForItem={showHoverPageCardForItem}
					closeAllHoverPageCards={closeAllHoverPageCards}
					clearHoverPageCardTimers={clearHoverPageCardTimers}
					startCloseHoverPageCardTimer={startCloseHoverPageCardTimer}
					onBlurHoverTarget={onBlurHoverTarget}
					shouldUseAutomationDiscovery
					key={showHoverPageCardForItem.itemID}
				/>
			) : null}
			{persistenceError && <ErrorDisplay error={persistenceError} />}
		</SpacePersistentCollapsibleSectionLoader>
	);
};

export const SpaceViewsController: FC<SpaceViewsControllerProps> = (props) => (
	<ErrorBoundary attribution={Attribution.DISCO}>
		{SpaceViewsControllerComponent({ ...props })}
	</ErrorBoundary>
);
